import { useCallback, useEffect, useRef, useState } from 'react';
import { collabApi, getCollabWhere, onChatRequest } from '../utils/collab';

/**
 * The single collaboration poll, and all the state it feeds.
 *
 * ONE timer, ONE endpoint. Presence, new messages and unread counts arrive together
 * because three timers polling three endpoints against one shared SQLite file
 * (Critical Note #17) is how this gets expensive for every other tenant on the box.
 *
 * Three rules this file exists to keep:
 *
 * 1. **The effect has no changing dependencies.** `App` re-renders every second
 *    (Critical Note #59); an effect that depended on a callback created up there would
 *    restart the poll sixty times a minute. Everything the loop needs lives in refs,
 *    and the loop is started exactly once.
 * 2. **A hidden tab does not poll.** `visibilitychange` parks it — a backgrounded tab
 *    must not heartbeat for eight hours.
 * 3. **The unread count is whatever the server said.** It is never decremented locally;
 *    clearing it is an explicit `markRead` round-trip (Critical Note #72).
 */

const IDLE_MS = 5000;   // nothing open
const ACTIVE_MS = 2000; // a thread is open
const BACKOFF_MS = 30000; // after a failure — do not hammer a backend that is down

// One page of history. The dock opens on the newest page and walks backwards from there.
const PAGE = 100;

// Fallback only. The real number arrives on every poll (`edit_window_seconds`) from the
// server that actually enforces it — this is what the first render uses before the first
// poll lands, and it must not be the thing anyone tunes.
const DEFAULT_EDIT_WINDOW = 24 * 60 * 60;

/**
 * The order messages go in.
 *
 * `seq` is the server's row id in insert order. It is used in preference to `created_at`
 * because every stamp this backend writes has ONE-SECOND resolution, so two messages
 * sent in the same second compare equal and the sort leaves them wherever they happened
 * to land — which is how a reply comes to sit above the thing it replies to. The stamp
 * is the tie-break only for a row old enough to predate `seq` being sent at all.
 */
const bySeq = (a, b) => (
  (a.seq && b.seq)
    ? a.seq - b.seq
    : String(a.created_at).localeCompare(String(b.created_at))
);

export default function useCollab({ enabled = true } = {}) {
  const [state, setState] = useState({
    ready: false,
    enabled: false,
    allowFollowMe: true,
    retentionDays: 90,
    me: null,
    people: [],
    editWindowSeconds: DEFAULT_EDIT_WINDOW,
    error: '',
  });
  // Threads, keyed by the OTHER person's user_id. Messages accumulate from the poll.
  const [threads, setThreads] = useState({});
  // Per-thread paging state, keyed the same way as `threads`:
  //   hasMoreBefore — there is older history than what is loaded ("Load earlier").
  //   hasMoreAfter  — what is loaded is a WINDOW out of the middle of the conversation,
  //                   opened from a search hit, so the newest messages are NOT on screen.
  //   anchorId      — the searched-for message, to scroll to and flash once.
  const [threadMeta, setThreadMeta] = useState({});
  const [openWith, setOpenWith] = useState(null);

  const cursorRef = useRef('');
  // `absorb` runs inside the poll, which has no changing dependencies (rule 1), so it
  // cannot close over `threadMeta`. It reads the same value through a ref instead.
  const metaRef = useRef({});
  useEffect(() => { metaRef.current = threadMeta; }, [threadMeta]);
  // `loadEarlier` needs the OLDEST loaded message to ask for the page before it, and it
  // must not be recreated whenever a message arrives — `App` re-renders every second
  // (Critical Note #59) and the dock hangs its handlers off these callbacks.
  const threadsRef = useRef({});
  useEffect(() => { threadsRef.current = threads; }, [threads]);
  const openWithRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);
  const enabledRef = useRef(enabled);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { openWithRef.current = openWith; }, [openWith]);

  /** Merge messages into their threads, keyed by id.
   *
   *  Three things this has to get right:
   *
   *  * **De-duplication.** The poll returns BOTH directions, so a message sent from
   *    another tab of the same browser lands here too and would otherwise appear twice.
   *  * **Replacement, not just insertion.** The poll's cursor is `updated_at`, so an
   *    EDITED or DELETED message arrives again with the same id. Matching by id and
   *    overwriting is what turns that into a correction on screen rather than a
   *    duplicate bubble below the original.
   *  * **Not appending into a WINDOW.** When a thread is showing a slice from the middle
   *    of the conversation (a search hit), a message that arrives now does not belong at
   *    the bottom of it — the bottom of that window is last March. It is skipped; the
   *    unread dot still reports it, and "Jump to latest" is what goes and gets it.
   */
  const absorb = useCallback((messages, meId) => {
    if (!messages || !messages.length) return;
    setThreads((prev) => {
      const next = { ...prev };
      messages.forEach((m) => {
        const other = m.from_user === meId ? m.to_user : m.from_user;
        const list = next[other] ? [...next[other]] : [];
        const at = list.findIndex((x) => x.id === m.id);
        if (at >= 0) {
          list[at] = m;
        } else {
          if (metaRef.current[other]?.hasMoreAfter) return;
          list.push(m);
        }
        list.sort(bySeq);
        next[other] = list;
      });
      return next;
    });
  }, []);

  /** Put one corrected message back where it already is. Unlike `absorb` this NEVER
   *  inserts: an edit is a replacement, and a message that is not on screen has nothing
   *  to correct — it will arrive whole on the next poll. */
  const absorbEdit = useCallback((m) => {
    setThreads((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        const at = next[key].findIndex((x) => x.id === m.id);
        if (at >= 0) {
          const list = [...next[key]];
          list[at] = m;
          next[key] = list;
        }
      });
      return next;
    });
  }, []);

  // ---- the loop. Mounted once, torn down once. ----
  useEffect(() => {
    stoppedRef.current = false;

    const schedule = (ms) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(tick, ms);
    };

    async function tick() {
      if (stoppedRef.current) return;
      // Parked while the tab is in the background. Re-armed by the visibility handler
      // below rather than by a timer, so a hidden tab costs nothing at all.
      if (document.visibilityState === 'hidden') return;
      if (!enabledRef.current) { schedule(IDLE_MS); return; }

      const where = getCollabWhere();
      try {
        const data = await collabApi.poll({
          since: cursorRef.current,
          module: where.module,
          panelId: where.panelId,
        });
        if (stoppedRef.current) return;

        if (data.cursor) cursorRef.current = data.cursor;
        const meId = data.me?.user_id || '';
        absorb(data.messages, meId);

        setState((prev) => ({
          ...prev,
          ready: true,
          enabled: !!data.enabled,
          allowFollowMe: data.allow_follow_me !== false,
          retentionDays: data.retention_days ?? 90,
          editWindowSeconds: data.edit_window_seconds ?? DEFAULT_EDIT_WINDOW,
          me: data.me || prev.me,
          people: data.people || [],
          // Doc Review's bell + nav badge counts ride on this poll (null = not licensed).
          review: data.review || null,
          error: '',
        }));
        schedule(openWithRef.current ? ACTIVE_MS : IDLE_MS);
      } catch (err) {
        if (stoppedRef.current) return;
        // A 401 means the session went away. Stop entirely — the app's own session
        // handling owns that conversation, and a poll that kept firing would put a
        // failed request in the console every five seconds behind the login screen.
        if (err.status === 401) {
          setState((prev) => ({ ...prev, ready: true, enabled: false }));
          return;
        }
        setState((prev) => ({ ...prev, ready: true }));
        schedule(BACKOFF_MS);
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule(0);
    };
    document.addEventListener('visibilitychange', onVisible);
    schedule(0);

    return () => {
      stoppedRef.current = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // Deliberately empty: see rule 1 in the module comment. `absorb` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Open a thread and clear its dot. The mark-read is EXPLICIT — the dot is server
   *  state, so it clears when the user opens the conversation, not when a poll
   *  happened to carry the message (Critical Note #72). */
  const open = useCallback(async (userId, pendingContext) => {
    setOpenWith(userId ? { userId, pendingContext: pendingContext || null } : null);
    if (!userId) return;
    try {
      const data = await collabApi.thread(userId, { limit: PAGE });
      setThreads((prev) => ({ ...prev, [userId]: data.messages || [] }));
      setThreadMeta((prev) => ({
        ...prev,
        [userId]: { hasMoreBefore: !!data.has_more_before, hasMoreAfter: false, anchorId: '' },
      }));
    } catch (_) { /* the poll will fill it in */ }
    try {
      await collabApi.markRead(userId);
      setState((prev) => ({
        ...prev,
        people: prev.people.map((p) => (p.user_id === userId ? { ...p, unread: 0 } : p)),
      }));
    } catch (_) { /* the next poll re-reads the truth */ }
  }, []);

  /** The page BEFORE what is loaded, prepended. Returns how many arrived, so the dock
   *  can keep the reader's place instead of jumping. */
  const loadEarlier = useCallback(async (userId) => {
    const list = threadsRef.current[userId] || [];
    if (!list.length) return 0;
    // `seq` is the row's id in insert order, NOT a timestamp: chat stamps have
    // one-second resolution, so paging on `created_at` silently drops every other
    // message that shares the boundary second. See `db.chat_thread`.
    const data = await collabApi.thread(userId, { limit: PAGE, before: list[0].seq });
    const older = data.messages || [];
    setThreads((prev) => {
      const current = prev[userId] || [];
      const seen = new Set(current.map((m) => m.id));
      return { ...prev, [userId]: [...older.filter((m) => !seen.has(m.id)), ...current] };
    });
    setThreadMeta((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), hasMoreBefore: !!data.has_more_before },
    }));
    return older.length;
  }, []);

  /** Replace the thread with a window centred on one message — what a search hit opens.
   *  `anchorId` rides along in the meta so the dock knows what to scroll to and flash;
   *  it is cleared by the dock once it has. */
  const jumpToMessage = useCallback(async (userId, messageId) => {
    const data = await collabApi.thread(userId, { around: messageId });
    setThreads((prev) => ({ ...prev, [userId]: data.messages || [] }));
    setThreadMeta((prev) => ({
      ...prev,
      [userId]: {
        hasMoreBefore: !!data.has_more_before,
        hasMoreAfter: !!data.has_more_after,
        anchorId: data.anchor_id || messageId,
      },
    }));
  }, []);

  /** Back to the live end of the conversation from a window. */
  const jumpToLatest = useCallback(async (userId) => {
    const data = await collabApi.thread(userId, { limit: PAGE });
    setThreads((prev) => ({ ...prev, [userId]: data.messages || [] }));
    setThreadMeta((prev) => ({
      ...prev,
      [userId]: { hasMoreBefore: !!data.has_more_before, hasMoreAfter: false, anchorId: '' },
    }));
  }, []);

  const clearAnchor = useCallback((userId) => {
    setThreadMeta((prev) => (prev[userId]?.anchorId
      ? { ...prev, [userId]: { ...prev[userId], anchorId: '' } }
      : prev));
  }, []);

  const searchThread = useCallback((userId, query) =>
    collabApi.searchThread(userId, query).then((d) => d.messages || []), []);

  /** Edit and delete both hand back the SERVER's version of the row and absorb it, so the
   *  "(edited)" mark and the tombstone come from the same shape the other browser will
   *  receive on its next poll — never from an optimistic guess made here. */
  const editMessage = useCallback(async (messageId, body) => {
    const data = await collabApi.editMessage(messageId, body);
    if (data?.message) absorbEdit(data.message);
    return data?.message;
  }, [absorbEdit]);

  const deleteMessage = useCallback(async (messageId) => {
    const data = await collabApi.deleteMessage(messageId);
    if (data?.message) absorbEdit(data.message);
    return data?.message;
  }, [absorbEdit]);

  const close = useCallback(() => setOpenWith(null), []);

  const send = useCallback(async (userId, body, context) => {
    const data = await collabApi.send(userId, body, context);
    if (data?.message) absorb([data.message], state.me?.user_id || '');
    return data?.message;
  }, [absorb, state.me]);

  // A "Discuss this row" from anywhere in the tree (see utils/collab.js).
  useEffect(() => onChatRequest((userId, context) => { open(userId, context); }), [open]);

  return {
    ...state,
    threads,
    threadMeta,
    openWith,
    open,
    close,
    send,
    loadEarlier,
    jumpToMessage,
    jumpToLatest,
    clearAnchor,
    searchThread,
    editMessage,
    deleteMessage,
    totalUnread: state.people.reduce((n, p) => n + (p.unread || 0), 0),
  };
}
